import React, { useEffect, useMemo } from "react";
import { t } from "i18next";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import C from "Constants";
import { UTILS } from "utils";
import { OrderAction } from "store/actions";
import { WhatsappIcon, PhoneIcon } from "icons";
import { TranslationText } from "components/TextTranslationContainer";
import { PageHeaderV2 } from "components/PageHeader";
import { ErrorComponent } from "components";
import { Spinner } from "components";
import TrackOrderStatus from "./TrackOrderStatus";
import {PendingIcon, StepCompletedIcon, ProgressIcon, DeliveredIcon, InTransientIcon} from "./status-icons";

const STEP_STATE = {
  ACCEPTED: {
    active: ["PREPARING"],
    completed: ["ON_THE_WAY", "COMPLETE"],
  },
  PREPARING: {
    active: ["PREPARING"],
    completed: ["ON_THE_WAY", "COMPLETE"],
  },
  ON_THE_WAY: {
    active: ["ON_THE_WAY"],
    completed: ["COMPLETE"],
  },
  DELIVERED: {
    active: ["COMPLETE"],
    completed: ["COMPLETE"],
  },
};

const ORDER_STEPS = (isDine) => [
  { key: "ACCEPTED", label: "Accepted" },
  { key: "PREPARING", label: "Preparing" },
  { key: "ON_THE_WAY", label: isDine ? "Ready for service" : "On the way" },
  { key: "DELIVERED", label: isDine ? "Served" : "Delivered" },
];

const getStepStatus = (stepKey, currentStatus) => {
  const step = STEP_STATE[stepKey];

  if (!step) return "upcoming";

  if (step.completed.includes(currentStatus)) return "completed";
  if (step.active.includes(currentStatus)) return "active";

  return "upcoming";
};

const getStepIcon = (stepKey, orderStatus) => {
  switch (orderStatus) {
    case "PENDING":
      return PendingIcon;

    case "PREPARING":
      if (stepKey === "ACCEPTED") return StepCompletedIcon;
      if (stepKey === "PREPARING") return ProgressIcon;
      if (stepKey === "ON_THE_WAY") return InTransientIcon;
      if (stepKey === "DELIVERED") return PendingIcon;
      break;

    case "ON_THE_WAY":
      if (stepKey === "ACCEPTED") return StepCompletedIcon;
      if (stepKey === "PREPARING") return StepCompletedIcon;
      if (stepKey === "ON_THE_WAY") return ProgressIcon;
      if (stepKey === "DELIVERED") return InTransientIcon;
      break;

    case "COMPLETE":
      if (stepKey === "DELIVERED") return DeliveredIcon;
      return StepCompletedIcon;

    default:
      return PendingIcon;
  }
};

export const StepTracking = ({ orderStatus, storeName, isDine = false }) => {

  const name = storeName || process.env.REACT_APP_COMPANY_NAME;


  const statusMessages = {
    PENDING: "is reviewing your order.",
    ACCEPTED: "has accepted your order.",
    PREPARING: "is currently preparing your order.",
    ON_THE_WAY: "rider is on the way to your location.",
    COMPLETE: "Your order has been served. Enjoy!",
  };

  const steps = ORDER_STEPS(isDine);


  return (
    <div className="w-full flex flex-col gap-2">
      <div className="flex items-center justify-between w-full max-w-md mx-auto">
        {steps.map((step, index) => {
         const Icon = getStepIcon(step.key, orderStatus);
         const state = getStepStatus(step.key, orderStatus);
         const isActive = state === "active";
     
         // 👇 next step logic
         const nextStep = steps[index + 1];
         const previousStep = steps[index - 1];
         const previousIcon = previousStep
           ? getStepIcon(previousStep.key, orderStatus)
           : null;
         const NextIcon = nextStep
           ? getStepIcon(nextStep.key, orderStatus)
           : null;

         const previousStepCompleted =  NextIcon === StepCompletedIcon || previousIcon === StepCompletedIcon
     
         const lineColor = NextIcon &&  NextIcon === InTransientIcon
           ? "bg-brand-light-base-primary"
           : isActive || previousStepCompleted
           ? "bg-brand-base-primary"
           : "bg-gray-300"; 
     

          return (
            <React.Fragment key={step.key}>
              {/* Step Circle */}
            <div className="flex flex-col items-center">
             <Icon />
              <TranslationText
                className={`mt-1 text-12x ${previousStepCompleted ? "text-brand-base-primary font-medium":isActive ? "text-brand-base-primary font-semibold" : "text-dark-blue-900 font-normal"}`}
                text={step?.label?.toLowerCase()}
              />
            </div>
            {/* connect the steps with a line */}
            {index < steps.length - 1 && (
          <div
            className={`h-1 flex-1 mx-2 ${lineColor}`}
            style={{ marginBottom: "25px" }}
          />
        )}
          </React.Fragment> 
        )})}
      </div>
      <p>
        <span className="font-semibold text-brand-base-primary">{name}</span>
        <TranslationText
          text={statusMessages[orderStatus]}
          className="text-14 font-medium"
        />
      </p>
    </div>
  );
};

const TrackOrder = ({ goBack = () => {}, goToLogin = () => {}, goToSettings = () => {}, children }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const history = useHistory();

  const { isLoading } = useSelector((state) => state?.loader);
  const { loading } = useSelector((state) => state?.brand);
  const { orderStatus, orderInfo, error } = useSelector((state) => state.order);

  const { selectedStore } = useSelector((state) => state?.store);
  const { DomainType } = useSelector((state) => state.AppReducer);
  const { brandInfo } = useSelector((state) => state.brandStorageReducer);


  useEffect(() => {
    if (params?.orderId) {
      if (params?.domainName && DomainType === C.TEMPORARAY_DOMAIN) {
        const orderId = params.orderId;
        const obj = {
          orderId,
          history,
        };
        dispatch(OrderAction.getOrderStatus(obj));
      } else {
        const orderId = params.orderId;
        const obj = {
          orderId,
          history,
        };
        dispatch(OrderAction.getOrderStatus(obj));
      }
    }
  }, []);

  const getOrderStatus = (orderInfo) => {
    if (orderInfo) {
      const status = UTILS.getOrderStatus(orderInfo);
      const isDine = orderInfo?.orderType?.type === "DINE_IN" || window.location.pathname.includes("/dine-in/");
      console.log("isDine", isDine);
      if (status)
        return (
          <StepTracking
            orderStatus={orderInfo?.orderStatus}
            isDine={isDine}
            storeName={ selectedStore?.baseLanguage?.name ??
              orderInfo?.storeInfo?.baseLanguage?.name ?? process.env.REACT_APP_COMPANY_NAME}
          />
        );
      else
        return (
          <TrackOrderStatus orderStatus={orderStatus} orderInfo={orderInfo} />
        );
    }
  };

  const orderDate = useMemo(() => {
    const date = orderInfo?.orderPlacedTime
    if (typeof date === "number" && date.toString().length === 10) {
      return moment.unix(date).format("DD MMM YYYY HH:mm");
    }
    // Fallback to regular moment parsing
    return moment(date).format("DD MMM YYYY HH:mm");
  }, [orderInfo]);

  const options = [];

  if (selectedStore?.whatsAppContact || orderInfo?.storeInfo?.whatsAppContact) {
    options.push({
      icon: <WhatsappIcon />,
      value:
        selectedStore?.whatsAppContact || orderInfo?.storeInfo?.whatsAppContact,
      label: "Whatsapp",
      link: `https://wa.me/${
        selectedStore?.whatsAppContact || orderInfo?.storeInfo?.whatsAppContact
      }`,
      hoverClass: "hover:bg-brand-base-primary",
    });
  }

  if (selectedStore?.mobileNumber || orderInfo?.storeInfo?.mobileNumber) {
    options.push({
      icon: (
        <PhoneIcon />
      ),
      value: selectedStore?.mobileNumber || orderInfo?.storeInfo?.mobileNumber,
      label: "Phone",
      link: `tel:${
        selectedStore?.mobileNumber || orderInfo?.storeInfo?.mobileNumber
      }`,
       hoverClass:"hover:bg-brand-base-primary hover:text-white"
    });
  }

  if (isLoading || loading) return <Spinner />;

  return (
    <div className="relative flex flex-col gap-8 items-center p-5">
      <PageHeaderV2
        pageTitle={"Track Your Order"}
        onGoBack={goBack}
        authClick={goToLogin}
        userClick={goToSettings}
        containerStyleProps={{
          className: "relative",
          style: {
            padding: "0",
          },
        }}
        headerStyleProps={{
          style: {
            padding: "0",
          },
        }}

        // hideAuth={true}
      />
      <div className="w-full flex flex-col gap-6">
        <div className="flex justify-between items-center w-full">
          <h3 className="text-dark-blue-700 text-14x">
            {t("Order Date")}
            <span> : </span>
            <span className="font-bold">
              {orderDate}
            </span>
          </h3>
          <h3 className="text-dark-blue-700 text-14x">
            {t("Order ID")}
            <span> : </span>
            <span className="font-bold">{orderInfo?.orderNumber}</span>
          </h3>
        </div>

        {error && <ErrorComponent error={error} />}
        {getOrderStatus(orderInfo)}
        <hr className="my-2 border-gray-200" />
        <div className="flex justify-between gap-2">
          <div className="flex gap-2 items-center">
            <img
              src={brandInfo?.setting?.brandLogoFirst || C.PLACEHODERIMAGE}
              alt={brandInfo?.brand?.brandName || process.env.REACT_APP_COMPANY_NAME}
              className="w-10 h-10 rounded-8x object-cover"
            />
            <h2 className="m-0 text-14x font-medium text-natural-900 font-poppins">
              {t(
                selectedStore?.baseLanguage?.name ??
                orderInfo?.storeInfo?.baseLanguage?.name ??
                  process.env.REACT_APP_COMPANY_NAME
              )}
            </h2>
          </div>
          <div className="flex gap-2 items-center">
            {options.map((option, index) => (
              <a
                href={option?.link}
                key={index}
                className={` w-11 h-11 rounded-full bg-svg-stroke flex items-center justify-center ${option?.hoverClass}`}
              >
                {option?.icon}
              </a>
            ))}
          </div>
        </div>
      </div>
      {children ? children : null}
    </div>
  );
};

export default TrackOrder;
